import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import { newsList } from "@/mocks/news";
import { formatDate } from "@/utils/dramaUtils";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetailPage() {
  const { newsId = "" } = useParams();
  const item = newsList.find((n) => n.id === newsId);

  useSeo({
    title: item ? `${item.title} - 赏金女王影视资讯` : "影视资讯详情 - 赏金女王",
    description: item ? item.summary : "影视资讯详情页",
    keywords: item ? `${item.category},影视资讯,赏金女王,行业动态` : "赏金女王",
  });

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-4">
          <i className="ri-file-unknow-line" aria-hidden="true"></i>
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该资讯内容</h1>
        <p className="mt-2 text-sm text-foreground-600">该资讯可能已被移除或链接有误</p>
        <div className="mt-6 flex justify-center gap-3">
          <Link
            to="/news"
            className="px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回资讯列表
          </Link>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const related = item.relatedIds
    .map((rid) => newsList.find((n) => n.id === rid))
    .filter((n): n is (typeof newsList)[number] => Boolean(n));

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <div className="mb-6">
        <Breadcrumb items={[{ name: "首页", path: "/" }, { name: "影视资讯", path: "/news" }, { name: item.category }]} />
      </div>

      <article className="max-w-4xl">
        <header className="mb-6">
          <span className="inline-block px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-900 mb-3">
            {item.category}
          </span>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
            {item.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              发布时间：{formatDate(item.date)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line" aria-hidden="true"></i>
              {item.category}
            </span>
          </div>
        </header>

        <div className="w-full rounded-lg overflow-hidden mb-8 bg-primary-900">
          <img
            src={item.cover}
            alt={`${item.title} 配图`}
            title={`${item.title} 影视资讯配图`}
            className="w-full h-auto max-h-[420px] object-cover object-top"
          />
        </div>

        <div className="space-y-5 text-sm md:text-base text-foreground-700 leading-7 md:leading-8">
          <p className="font-medium text-foreground-900 pl-4 border-l-4 border-accent-500 bg-background-100/60 py-3 rounded-r-md">
            {item.summary}
          </p>
          {item.content.map((para, index) => (
            <p key={`${item.id}-p-${index}`}>{para}</p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/news"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回资讯列表
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="mt-14 max-w-4xl">
          <SectionHeading title="相关文章推荐" subtitle="与本文主题相关的更多资讯" />
          <div className="space-y-3 md:space-y-4">
            {related.map((r) => (
              <NewsCard key={r.id} item={r} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}